import {
  Badge,
  Box,
  Button,
  Circle,
  Divider,
  Flex,
  HStack,
  Icon,
  IconButton,
  Popover,
  PopoverArrow,
  PopoverBody,
  PopoverContent,
  PopoverTrigger,
  Skeleton,
  Text,
  VStack,
} from '@chakra-ui/react';
import { motion } from 'framer-motion';
import { formatDistanceToNow } from 'date-fns';
import esLocale from 'date-fns/locale/es';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FiBell,
  FiCheckCircle,
  FiEdit3,
  FiGlobe,
  FiMonitor,
  FiPlusCircle,
  FiXCircle,
} from 'react-icons/fi';
import { EventoReserva, TipoEvento, momentoDe } from '~/core/tracking/reservationEvents';
import { useActividadEmpresa } from '~/hooks/useReservationEvents';
import { useAuth } from '~/hooks/useAuth';

const MotionCircle = motion(Circle);

const ASPECTO: Record<TipoEvento, { icono: typeof FiPlusCircle; color: string }> = {
  created: { icono: FiPlusCircle, color: 'green' },
  modified: { icono: FiEdit3, color: 'orange' },
  cancelled: { icono: FiXCircle, color: 'red' },
  status: { icono: FiCheckCircle, color: 'blue' },
};

const CLAVE_VISTO = 'actividad:ultimoVisto';

/**
 * Campana de actividad real.
 *
 * Sustituye a un aviso que estaba escrito a mano —siempre el mismo texto sobre
 * el check-in y un "1" fijo en el contador— por lo que de verdad está pasando:
 * las últimas reservas creadas, modificadas y canceladas, en tiempo real.
 *
 * El contador marca lo ocurrido desde la última vez que se abrió, guardado en
 * este navegador. Es una comodidad, no un dato compartido: si se pierde, la
 * campana simplemente vuelve a partir de cero.
 */
const CampanaActividad = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { eventos, cargando } = useActividadEmpresa(user?.company?.id, 40);

  const [ultimoVisto, setUltimoVisto] = useState<number>(() => {
    try {
      return Number(localStorage.getItem(CLAVE_VISTO)) || 0;
    } catch {
      return 0;
    }
  });

  const recientes = useMemo(() => eventos.slice(0, 12), [eventos]);
  const sinVer = useMemo(
    () => eventos.filter((e) => momentoDe(e) > ultimoVisto).length,
    [eventos, ultimoVisto]
  );

  // La primera vez no tiene sentido anunciar meses de historial como "nuevo".
  useEffect(() => {
    if (ultimoVisto === 0 && eventos.length > 0) {
      const ahora = Date.now();
      setUltimoVisto(ahora);
      try {
        localStorage.setItem(CLAVE_VISTO, String(ahora));
      } catch {
        /* modo privado: se queda solo en memoria */
      }
    }
  }, [eventos.length, ultimoVisto]);

  const marcarVisto = () => {
    const ahora = Date.now();
    setUltimoVisto(ahora);
    try {
      localStorage.setItem(CLAVE_VISTO, String(ahora));
    } catch {
      /* sin almacenamiento: no pasa nada */
    }
  };

  return (
    <Popover placement='bottom-end' onOpen={marcarVisto} isLazy>
      <PopoverTrigger>
        <Box position='relative' cursor='pointer'>
          <IconButton
            size='lg'
            variant='ghost'
            aria-label={sinVer > 0 ? `${sinVer} novedades` : 'Actividad reciente'}
            icon={<FiBell />}
          />
          {sinVer > 0 && (
            <MotionCircle
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ type: 'spring', stiffness: 500, damping: 22 }}
              position='absolute'
              top='4px'
              right='2px'
              size='18px'
              bg='red.500'
              color='white'
              fontSize='10px'
              fontWeight='bold'
              pointerEvents='none'
            >
              {sinVer > 9 ? '9+' : sinVer}
            </MotionCircle>
          )}
        </Box>
      </PopoverTrigger>

      <PopoverContent w='380px' shadow='lg' _focus={{ outline: 'none' }}>
        <PopoverArrow />
        <PopoverBody p={0}>
          <Flex px={4} py={3} align='center' justify='space-between'>
            <Text fontSize='sm' fontWeight='semibold'>
              Actividad reciente
            </Text>
            <HStack spacing={1.5} color='green.500'>
              <MotionCircle
                size='6px'
                bg='green.400'
                animate={{ opacity: [1, 0.25, 1] }}
                transition={{ duration: 2, repeat: Infinity }}
              />
              <Text fontSize='xs'>en vivo</Text>
            </HStack>
          </Flex>
          <Divider />

          <Box maxH='380px' overflowY='auto'>
            {cargando && (
              <VStack align='stretch' spacing={2} p={4}>
                {[0, 1, 2].map((i) => (
                  <Skeleton key={i} h='40px' borderRadius='md' />
                ))}
              </VStack>
            )}

            {!cargando && recientes.length === 0 && (
              <VStack py={10} spacing={1} color='gray.500'>
                <Icon as={FiBell} boxSize={6} color='gray.300' />
                <Text fontSize='sm'>Aún no hay actividad.</Text>
              </VStack>
            )}

            {!cargando &&
              recientes.map((evento: EventoReserva, i) => {
                const aspecto = ASPECTO[evento.type] ?? ASPECTO.status;
                const ms = momentoDe(evento);
                const nuevo = ms > ultimoVisto;
                return (
                  <Box
                    key={evento.id ?? i}
                    px={4}
                    py={2.5}
                    bg={nuevo ? 'blue.50' : 'transparent'}
                    _hover={{ bg: nuevo ? 'blue.100' : 'gray.50' }}
                    borderBottomWidth='1px'
                    borderColor='gray.50'
                  >
                    <Flex gap={2.5} align='flex-start'>
                      <Circle size='26px' bg={`${aspecto.color}.50`} flexShrink={0} mt='2px'>
                        <Icon as={aspecto.icono} color={`${aspecto.color}.500`} boxSize={3.5} />
                      </Circle>
                      <Box flex={1} minW={0}>
                        <Text fontSize='xs' color='gray.800' noOfLines={2}>
                          {evento.summary}
                        </Text>
                        <HStack spacing={2} mt={0.5} color='gray.400'>
                          {evento.code && (
                            <Badge colorScheme='blue' variant='subtle' fontSize='0.55rem'>
                              {evento.code}
                            </Badge>
                          )}
                          <Icon
                            as={evento.origin?.channel === 'dashboard' ? FiMonitor : FiGlobe}
                            boxSize={3}
                          />
                          <Text fontSize='0.65rem'>
                            {ms ? formatDistanceToNow(new Date(ms), { locale: esLocale, addSuffix: true }) : ''}
                          </Text>
                        </HStack>
                      </Box>
                    </Flex>
                  </Box>
                );
              })}
          </Box>

          <Divider />
          <Box p={2}>
            <Button
              size='sm'
              variant='ghost'
              colorScheme='blue'
              w='full'
              onClick={() => navigate('/actividad')}
            >
              Ver toda la actividad
            </Button>
          </Box>
        </PopoverBody>
      </PopoverContent>
    </Popover>
  );
};

export default CampanaActividad;
